import { useMemo } from "react";
import { useInfiniteQuery } from "react-query";
import { reactQueryGet } from "@/react/helpers/backendRequestHelpers";
import { internalApi } from "@/react/helpers/urlHelpers/internalApi";
import type { Page } from "@/react/types/Page";
import { PAGES_QUERY_KEY } from "./usePage";

export const DEFAULT_PAGE_SIZE = 20;

interface ResponsePageProps {
  count: number;
  has_next_page: boolean;
  page: number;
  page_count: number;
  per_page: number;
  records: Page[];
}
interface UsePagesInfiniteQueryProps {
  initialPage: number;
  options?: {
    keepPreviousData?: boolean;
  };
  perPage: number;
  query?: string;
  status?: string;
}

export const usePagesInfiniteQuery = ({
  perPage = DEFAULT_PAGE_SIZE,
  initialPage = 1,
  query = "",
  status,
  options = {},
}: UsePagesInfiniteQueryProps) => {
  const { data, ...rest } = useInfiniteQuery(
    [...PAGES_QUERY_KEY, { perPage, query }],
    ({ pageParam = initialPage }) =>
      reactQueryGet(
        internalApi.settings.sites.pages.index({
          params: {
            page: pageParam,
            per_page: perPage,
            query,
            ...(status ? { status } : {}),
          },
        }),
      ),
    {
      getNextPageParam: lastPage =>
        lastPage?.has_next_page ? lastPage.page + 1 : undefined,
      ...options,
    },
  );

  const pages: ResponsePageProps[] = useMemo(
    () => data?.pages ?? [],
    [data?.pages],
  );
  const totalRecords = pages[0]?.count ?? 0;
  const pageSize = pages[0]?.per_page ?? perPage;

  return {
    data,
    totalRecords,
    pageSize,
    ...rest,
  };
};
